import { ActionPlanFeatureService } from '@/features/action-plan/action-plan-feature.service';
import { AlertsFeatureService } from '@/features/alerts/alerts-feature.service';
import { BuildingVisualizationFeatureService } from '@/features/building-visualization/building-visualization-feature.service';
import { BuildingsIndicatorsAnalysisFeatureService } from '@/features/buildings-indicators-analysis/buildings-indicators-analysis-feature.service';
import { BusinessIndicatorsFeatureService } from '@/features/business-indicators/business-indicators.service';
import { ContractPowerOptimizationFeatureService } from '@/features/contract-power-optimization/contract-power-optimization-feature.service';
import { DataQualityFeatureService } from '@/features/data-health/data-health-feature.service';
import { DataViewerFeatureService } from '@/features/data-viewer/data-viewer-feature.service';
import { DataboardsFeatureService } from '@/features/databoards/databoards-feature.service';
import { DeetFeatureService } from '@/features/deet/deet-feature.service';
import { HomepageFeatureService } from '@/features/homepage/homepage-feature.service';
import { IoTSupervisionFeatureService } from '@/features/iot-supervision/iot-supervision-feature.service';
import { MipFeatureService } from '@/features/mip/shared/mip-feature.service';
import { RecommendedActionsFeatureService } from '@/features/recommended-actions/recommended-actions-feature.service';
import { ReportsFeatureService } from '@/features/reports/reports-feature.service';
import { SenAnalysisFeatureService } from '@/features/sen-analysis/sen-analysis-feature.service';
import { BrowserProvider } from '@/providers/browser.provider';
import { ProfileProvider } from '@/providers/profile/profile.provider';
import {
    ACTIONS_RIGHT_ACCESS,
    DATA_ACCESS,
    DATA_QUALIFICATION_RIGHT_ACCESS,
    DEET_ADMIN_ACCESS,
    DEET_VIEWER_ACCESS,
    DOCUMENT_MANAGEMENT_SYSTEM_ACCESS,
    EXPORT_ACCESS,
    MANAGED_ENTITY_ACCESS,
    MAPS_RIGHT_ACCESS,
    NEW_DELIVERY_POINTS_PAGE,
    REPORT_ACCESS
} from '@/providers/rights.constants';
import { Page, RoutingService } from '@/services/routing/routing.service';
import { UiChipComponent } from '@/ui/chip/ui-chip';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { IconName, UiIconComponent } from '@/ui/icon/ui-icon';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, type OnInit, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedUiColorPalettePipe } from '../../../../libs/shared/ui/theming/lib/colors/shared-ui-color-palette.pipe';
import { BrandingProvider } from '../../../../providers/branding/branding.provider';
import { TutorialService } from '../../../../services/tutorial.service';

interface MenuItem {
    name$: Observable<string>;
    iconName: IconName;
    isExpandable: boolean;
    isExpanded: boolean;
    children: {
        name$: Observable<string>;
        click: () => void;
        isDisabled?: boolean;
        tagName?: string;
        iconName?: IconName;
        matchingPages: Page[];
    }[];
    click?: () => void;
    tagName?: string;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'left-menu',
    templateUrl: './left-menu.component.html',
    styleUrls: ['./left-menu.component.scss'],
    imports: [UiIconButtonComponent, UiIconComponent, UiChipComponent, AsyncPipe, SharedUiColorPalettePipe, TranslatePipe]
})
export class LeftMenuComponent implements OnInit {
    private _routingService = inject(RoutingService);
    private _browserProvider = inject(BrowserProvider);
    private _profileProvider = inject(ProfileProvider);
    private _translateService = inject(TranslateService);
    private _brandingProvider = inject(BrandingProvider);
    private _alertsFeatureService = inject(AlertsFeatureService);
    private _businessIndicatorsFeatureService = inject(BusinessIndicatorsFeatureService);
    private _databoardsFeatureService = inject(DataboardsFeatureService);
    private _buildingVisualizationFeatureService = inject(BuildingVisualizationFeatureService);
    private _tutorialService = inject(TutorialService);
    private _homepageFeatureService = inject(HomepageFeatureService);
    private _senAnalysisFeatureService = inject(SenAnalysisFeatureService);
    private _actionPlanFeatureService = inject(ActionPlanFeatureService);
    private _reportsFeatureService = inject(ReportsFeatureService);
    private _deetFeatureService = inject(DeetFeatureService);
    private _ioTSupervisionFeatureService = inject(IoTSupervisionFeatureService);
    private _mipFeatureService = inject(MipFeatureService);
    private _recommendedActionService = inject(RecommendedActionsFeatureService);
    private _contractPowerOptimization = inject(ContractPowerOptimizationFeatureService);
    private _dataViewerFeatureService = inject(DataViewerFeatureService);
    private _dataQualityFeatureService = inject(DataQualityFeatureService);
    private _buildingsIndicatorsAnalysisFeatureService = inject(BuildingsIndicatorsAnalysisFeatureService);

    isCollapsed = false;
    logo$!: Observable<string>;

    mainItems: MenuItem[] = [];

    currentPage = toSignal(this._routingService.getCurrentPage$());

    shouldDisplaySupportButton: Signal<Boolean> = toSignal(
        combineLatest([
            this._tutorialService.isTutorialEnabled$,
            this._profileProvider.isProjectAdmin$(),
            this._brandingProvider.hasBrandingAccessToAdvizeoSupport$(),
            this._brandingProvider.hasBrandingSimpleUserAccessToAdvizeoSupport$()
        ]).pipe(
            map(([isTutorialEnabled, isProjectAdmin, hasBrandingAccessToAdvizeoSupport, hasBrandingSimpleUserAccessToAdvizeoSupport]) => {
                return (
                    !isTutorialEnabled &&
                    (isProjectAdmin || hasBrandingSimpleUserAccessToAdvizeoSupport) &&
                    hasBrandingAccessToAdvizeoSupport
                );
            })
        ),
        { initialValue: false }
    );

    footerItems: Signal<{ name: string; iconName: IconName; click: () => void }[]> = computed(() =>
        this.shouldDisplaySupportButton()
            ? [
                  {
                      name: 'MENU.SUPPORT',
                      iconName: IconName.SUPPORT_LIGHT,
                      click: () => {
                          this._browserProvider.goTo('https://advizeo.atlassian.net/servicedesk/customer/portals');
                      }
                  }
              ]
            : []
    );

    constructor() {
        effect(() => {
            const currentPage = this.currentPage();
            if (isNullish(currentPage)) {
                return;
            }

            const itemToExpand = this.mainItems.find(
                item => item.isExpandable && item.children.some(child => child.matchingPages.includes(currentPage) && !item.isExpanded)
            );

            if (isNotNullish(itemToExpand)) {
                this.toggleItem(itemToExpand, false);
            }
        });
    }

    ngOnInit() {
        this.logo$ = combineLatest([this._brandingProvider.getBranding$(), this._brandingProvider.isNotCustomBranding$()]).pipe(
            map(([branding, isNotCustomBranding]) => {
                if (isNotCustomBranding) {
                    return '/assets/images/savee_logo_small_white.png';
                } else {
                    return branding.logoOnDarkUri;
                }
            })
        );
        combineLatest([
            combineLatest([
                this._profileProvider.hasRight$(MAPS_RIGHT_ACCESS),
                this._profileProvider.hasRight$(DATA_ACCESS),
                this._profileProvider.hasRight$(DOCUMENT_MANAGEMENT_SYSTEM_ACCESS)
            ]),
            combineLatest([
                this._alertsFeatureService.hasFeatureAccess$(),
                this._profileProvider.hasRight$(REPORT_ACCESS),
                this._businessIndicatorsFeatureService.hasFeatureAccess$()
            ]),
            combineLatest([this._profileProvider.hasRight$(ACTIONS_RIGHT_ACCESS), this._recommendedActionService.hasFeatureAccess$()]),
            combineLatest([this._ioTSupervisionFeatureService.hasFeatureAccess$(), this._contractPowerOptimization.hasMenuEntryCheck$()]),
            combineLatest([
                this._profileProvider.hasRight$(DATA_QUALIFICATION_RIGHT_ACCESS),
                this._buildingsIndicatorsAnalysisFeatureService.hasFeatureAccess$(),
                this._databoardsFeatureService.hasFeatureAccess$(),
                this._senAnalysisFeatureService.hasFeatureAccess$()
            ]),
            combineLatest([this._profileProvider.hasRight$(EXPORT_ACCESS), this._profileProvider.hasRight$(MANAGED_ENTITY_ACCESS)]),
            combineLatest([
                this._profileProvider.hasRight$(NEW_DELIVERY_POINTS_PAGE),
                this._buildingVisualizationFeatureService.hasFeatureAccess$()
            ]),
            combineLatest([
                this._deetFeatureService.hasFeatureAccess$(),
                this._profileProvider.hasRight$(DEET_VIEWER_ACCESS),
                this._profileProvider.hasRight$(DEET_ADMIN_ACCESS)
            ]),
            this._mipFeatureService.hasFeatureAccess$()
        ])
            .pipe(untilDestroyed(this))
            .subscribe(
                ([
                    [hasMapAccess, hasDataAccess, hasDocumentManagementAccess],
                    [hasAlertAccess, hasReportAccess, hasBusinessIndicatorAccess],
                    [hasActionsAccess, hasRecommendedActionAccess],
                    [hasIoTSupervisionAccess, hasContractPowerOptimizationAccess],
                    [hasDataQualificationAccess, hasComparisonAccess, hasDataboardAccess, hasSenAnalysisAccess],
                    [hasExportAccess, hasDataManagementAccess],
                    [hasNewDeliveryPointPageAccess, hasNewBuildingPageAccess],
                    [hasDeetFeatureAccess, hasDeetUserAcess, hasDeetAdminAccess],
                    hasMipFeatureAccess
                ]) => {
                    this.mainItems = [
                        {
                            name$: this._translateService.stream('MENU.HOME'),
                            iconName: IconName.HOME_LIGHT,
                            isExpandable: false,
                            isExpanded: false,
                            children: [],
                            click: () => this.goToHomepage()
                        }
                    ];

                    /////////////
                    // myStock //
                    /////////////
                    const myStockItems: MenuItem['children'] = [];

                    if (hasMapAccess) {
                        myStockItems.push({
                            name$: this._translateService.stream('MENU.MAP'),
                            click: () => {
                                this._routingService.resetNavigationTo(Page.MAPS, {});
                            },
                            matchingPages: [Page.MAPS]
                        });
                    }

                    if (hasNewBuildingPageAccess) {
                        myStockItems.push({
                            name$: this._translateService.stream('MENU.BUILDINGS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._buildingVisualizationFeatureService.goToFeature();
                            },
                            matchingPages: [Page.BUILDINGS]
                        });
                    }

                    if (hasNewDeliveryPointPageAccess) {
                        myStockItems.push({
                            name$: this._translateService.stream('MENU.DELIVERY_POINTS'),
                            click: () => {
                                this._routingService.resetNavigationTo(Page.DELIVERY_POINTS, {});
                            },
                            matchingPages: [Page.DELIVERY_POINTS, Page.BILLS_BY_DELIVERY_POINT]
                        });
                    }

                    if (hasIoTSupervisionAccess) {
                        myStockItems.push({
                            name$: this._translateService.stream('MENU.IOT_SUPERVISION'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._ioTSupervisionFeatureService.goToFeature();
                            },
                            matchingPages: [Page.IOT_SUPERVISION]
                        });
                    }

                    if (hasDataAccess) {
                        myStockItems.push({
                            name$: this._translateService.stream('MENU.DATA_SOURCES'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._dataViewerFeatureService.goToFeature();
                            },
                            matchingPages: [
                                Page.DATA_VIEWER,
                                Page.INVOICE_EDITION,
                                Page.SPECIFIC_MANUAL_INDEX_READINGS,
                                Page.SYSTEM_EDITION
                            ]
                        });
                    }

                    if (hasDocumentManagementAccess) {
                        myStockItems.push({
                            name$: this._translateService.stream('MENU.DOCUMENT_MANAGEMENT_SYSTEM'),
                            click: () => {
                                this._routingService.resetNavigationTo(Page.DOCUMENT_MANAGEMENT, {});
                            },
                            matchingPages: [Page.DOCUMENT_MANAGEMENT, Page.DOCUMENT_MANAGEMENT_IMPORT]
                        });
                    }

                    if (myStockItems.length) {
                        this.mainItems.push({
                            name$: this._translateService.stream('MENU.MY_STOCK'),
                            iconName: IconName.BUILDINGS,
                            isExpandable: true,
                            isExpanded: false,
                            children: myStockItems
                        });
                    }

                    ///////////
                    // track //
                    ///////////
                    const trackItems: MenuItem['children'] = [
                        {
                            name$: this._translateService.stream('MENU.HOME_DASHBOARD'),
                            click: () => {
                                this._routingService.resetNavigationTo(Page.DASHBOARDS, {});
                            },
                            matchingPages: [Page.DASHBOARDS, Page.BUDGET_EDITION, Page.BUDGET_FUNNEL_VIEW]
                        }
                    ];

                    if (hasReportAccess) {
                        trackItems.push({
                            name$: this._translateService.stream('MENU.REPORTS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._reportsFeatureService.goToFeature();
                            },
                            matchingPages: [Page.AUTOMATED_REPORTS_LIST, Page.AUTOMATED_REPORT_EDITION]
                        });
                    }

                    if (hasBusinessIndicatorAccess) {
                        trackItems.push({
                            name$: this._translateService.stream('MENU.BUSINESS_INDICATORS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._businessIndicatorsFeatureService.goToFeature();
                            },
                            matchingPages: [Page.BUSINESS_INDICATORS_DETAILS]
                        });
                    }

                    if (trackItems.length) {
                        this.mainItems.push({
                            name$: this._translateService.stream('MENU.TRACK'),
                            iconName: IconName.TRACK,
                            isExpandable: true,
                            isExpanded: false,
                            children: trackItems
                        });
                    }

                    ///////////
                    // steer //
                    ///////////
                    const steerItems: MenuItem['children'] = [];

                    if (hasActionsAccess) {
                        steerItems.push({
                            name$: this._translateService.stream('MENU.ACTIONS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._actionPlanFeatureService.goToFeature();
                            },
                            matchingPages: [Page.ACTIONS_LIST, Page.ACTION_CONSULTATION]
                        });
                    }

                    if (hasRecommendedActionAccess) {
                        steerItems.push({
                            name$: this._translateService.stream('MENU.RECOMMENDED_ACTIONS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._recommendedActionService.goToFeature();
                            },
                            matchingPages: [Page.RECOMMENDED_ACTIONS_LIST]
                        });
                    }

                    if (hasDeetFeatureAccess && (hasDeetUserAcess || hasDeetAdminAccess)) {
                        steerItems.push({
                            name$: this._translateService.stream('MENU.DEET'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._deetFeatureService.goToFeature();
                            },
                            tagName: 'Beta',
                            matchingPages: [Page.DEET_EFA_LANDING]
                        });
                    }
                    if (hasMipFeatureAccess) {
                        steerItems.push({
                            name$: this._translateService.stream('MENU.MIP'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._mipFeatureService.goToFeature();
                            },
                            tagName: 'Beta',
                            matchingPages: [Page.MIP]
                        });
                    }

                    if (hasContractPowerOptimizationAccess) {
                        steerItems.push({
                            name$: this._translateService.stream('MENU.CONTRACT_POWER_OPTIMIZATION'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._contractPowerOptimization.goToFeature();
                            },
                            matchingPages: [Page.CONTRACT_POWER_OPTIMIZATION]
                        });
                    }

                    if (steerItems.length) {
                        this.mainItems.push({
                            name$: this._translateService.stream('MENU.STEER'),
                            iconName: IconName.STEER,
                            isExpandable: true,
                            isExpanded: false,
                            children: steerItems
                        });
                    }

                    /////////////
                    // analyse //
                    /////////////
                    const analyzeItems: MenuItem['children'] = [];

                    if (hasSenAnalysisAccess) {
                        analyzeItems.push({
                            name$: this._translateService.stream('MENU.SEN_ANALYSIS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._senAnalysisFeatureService.goToFeature();
                            },
                            matchingPages: [Page.SEN_ANALYSIS_LANDING]
                        });
                    }

                    if (hasDataboardAccess) {
                        analyzeItems.push({
                            name$: this._translateService.stream('MENU.DATABOARDS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._databoardsFeatureService.goToFeature();
                            },
                            tagName: 'Beta',
                            matchingPages: [Page.DATABOARDS]
                        });
                    }

                    if (hasDataQualificationAccess) {
                        analyzeItems.push({
                            name$: this._translateService.stream('MENU.DATA_QUALIFICATION'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._dataQualityFeatureService.goToFeature();
                            },
                            matchingPages: [
                                Page.DATA_HEALTH_SYNTHESIS,
                                Page.DATA_HEALTH_TEMPORAL_COMPLETENESS,
                                Page.DATA_HEALTH_TEMPORAL_COMPLETENESS_DETAILS,
                                Page.DATA_HEALTH_COMPLETENESS,
                                Page.DATA_HEALTH_QUALITY
                            ]
                        });
                    }

                    if (hasComparisonAccess) {
                        analyzeItems.push({
                            name$: this._translateService.stream('MENU.BUILDINGS_COMPARISON'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._buildingsIndicatorsAnalysisFeatureService.goToFeature();
                            },
                            matchingPages: [Page.INDICATORS_BUILDINGS_COMPARISON]
                        });
                    }

                    if (hasAlertAccess) {
                        analyzeItems.push({
                            name$: this._translateService.stream('MENU.ALERTS'),
                            click: () => {
                                this._routingService.notifyNavigationReset();
                                this._alertsFeatureService.goToFeature();
                            },
                            matchingPages: [
                                Page.THRESHOLD_SCENARIO_LANDING,
                                Page.WATER_LEAKAGE_SCENARIO_LANDING,
                                Page.DRIFT_SCENARIO_LANDING
                            ]
                        });
                    }

                    if (analyzeItems.length) {
                        this.mainItems.push({
                            name$: this._translateService.stream('MENU.ANALYZE'),
                            iconName: IconName.ANALYZE,
                            isExpandable: true,
                            isExpanded: false,
                            children: analyzeItems
                        });
                    }

                    //////////////////
                    // importExport //
                    //////////////////
                    const importExportItems: MenuItem['children'] = [];

                    if (hasExportAccess) {
                        importExportItems.push({
                            name$: this._translateService.stream('MENU.EXPORT'),
                            click: () => {
                                this._routingService.resetNavigationTo(Page.EXPORT_LANDING, {});
                            },
                            matchingPages: [Page.EXPORT_LANDING]
                        });
                    }

                    if (hasDataManagementAccess) {
                        importExportItems.push({
                            name$: this._translateService.stream('MANAGED_ENTITY.MENU_TITLE'),
                            click: () => {
                                this._routingService.resetNavigationTo(Page.ENTITY_MANAGEMENT, {});
                            },
                            matchingPages: [Page.ENTITY_MANAGEMENT]
                        });
                    }

                    if (importExportItems.length) {
                        this.mainItems.push({
                            name$: this._translateService.stream('MENU.IMPORT_AND_EXPORT'),
                            iconName: IconName.IMPORT_AND_EXPORT,
                            isExpandable: true,
                            isExpanded: false,
                            children: importExportItems
                        });
                    }
                }
            );
    }

    goToHomepage() {
        this._homepageFeatureService.goToFeature();
    }

    toggleItem(item: MenuItem, openMenuIfCollapsed = true) {
        item.isExpanded = !item.isExpanded;
        if (item.isExpanded) {
            if (openMenuIfCollapsed) {
                this.isCollapsed = false;
            }
            for (const i of this.mainItems) {
                if (i !== item) {
                    i.isExpanded = false;
                }
            }
        }
    }

    collapseMenu() {
        this.isCollapsed = true;
    }
}
